import { useInfiniteQuery, useMutation, useQuery, useQueryClient, } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { joinURL } from "ufo";
import { getQueryClient } from "../../utils/query-client.js";
import { getRPCClient } from "../../utils/trpc-client.js";
import { apiConfig } from "../runtime/apiConfig.js";
class QueryError extends Error {
    name = "QueryError";
    messages;
    statusCode;
    constructor(args) {
        const msg = args.messages.join(", ");
        super(msg);
        this.statusCode = args.statusCode;
        this.messages = args.messages;
    }
}
function useFallbackClient() {
    return useQueryClient(getQueryClient());
}
function getApiKeyHeaders() {
    if (apiConfig.apiKey) {
        return {
            "x-ed-api-key": apiConfig.apiKey,
        };
    }
    else {
        return {};
    }
}
const fetchQuery = async (name, params, opts) => {
    let url;
    let options = {
        headers: {
            ...opts?.headers,
            ...getApiKeyHeaders(),
        },
    };
    if (env.serverless && env.client) {
        url = `/_data/query/${name}`;
    }
    else if (env.serverless) {
        url = joinURL(env.origin ?? "", `/wp-json/ed/v1/query/${name}`);
    }
    else {
        url = `/wp-json/ed/v1/query/${name}`;
    }
    if (params)
        url += "?params=" + encodeURIComponent(params);
    // Apply API configuration
    if (apiConfig.customQueryFetchOptions) {
        options = await apiConfig.customQueryFetchOptions?.("query", name, params, url, options);
    }
    const response = await fetch(url, options);
    if (apiConfig.onResponse)
        apiConfig.onResponse(response, "query", name, params, url, options);
    const payload = await response.json();
    if (payload.errors?.length > 0) {
        throw new QueryError({
            messages: payload.errors.map((e) => e.message),
            statusCode: response.status,
            queryName: name,
            type: "query",
        });
    }
    return payload.data;
};
export function createUseQuery(init) {
    const hook = (args, options) => {
        const customKey = apiConfig.customQueryKey;
        return useQuery({
            queryKey: ["query", init.name, args, options?.headers, customKey],
            queryFn: (args) => fetchQuery(init.name, args.queryKey[2] ? JSON.stringify(args.queryKey[2]) : "", {
                headers: options?.headers,
            }),
            ...options,
        }, useFallbackClient());
    };
    hook.fetch = async (args, options) => {
        const customKey = apiConfig.customQueryKey;
        return getQueryClient().fetchQuery({
            queryKey: ["query", init.name, args, options?.headers, customKey],
            queryFn: (args) => fetchQuery(init.name, args.queryKey[2] ? JSON.stringify(args.queryKey[2]) : "", {
                headers: options?.headers,
            }),
            ...options,
        });
    };
    return hook;
}
function selectByPath(data, path) {
    return path.reduce((acc, key) => (acc ? acc[key] : null), data);
}
export function createUseInfiniteQuery(init) {
    const hook = (args, options) => {
        const customKey = apiConfig.customQueryKey;
        const [initial, setInitial] = useState(() => {
            if (options?.initialData) {
                const data = options?.initialData;
                const endCursor = selectByPath(data, init.paths.endCursorPath);
                const hasNextPage = selectByPath(data, init.paths.hasNextPagePath);
                const nodes = selectByPath(data, init.paths.nodesPath);
                if (!nodes)
                    return {};
                return {
                    initialData: {
                        pages: [{ nodes, endCursor, hasNextPage }],
                        pageParams: [],
                    },
                };
            }
        });
        useEffect(() => {
            setInitial({
                initialData: undefined,
            });
        }, []);
        return useInfiniteQuery({
            queryKey: ["query", init.name, args, init.defaultLimit, options?.headers, customKey],
            queryFn: async (args) => {
                const finalArgs = {
                    limit: args.queryKey[3],
                    ...(args.queryKey[2] ?? {}),
                    cursor: args.pageParam,
                };
                const data = await fetchQuery(init.name, JSON.stringify(finalArgs), {
                    headers: options?.headers,
                });
                const endCursor = selectByPath(data, init.paths.endCursorPath);
                const hasNextPage = selectByPath(data, init.paths.hasNextPagePath);
                const nodes = selectByPath(data, init.paths.nodesPath);
                const total = selectByPath(data, [...init.paths.pageInfoPath, "total"]);
                return {
                    nodes,
                    hasNextPage,
                    endCursor,
                    total,
                };
            },
            initialPageParam: "",
            getNextPageParam: (lastPage) => {
                // @ts-ignore
                return lastPage.hasNextPage ? lastPage.endCursor : undefined;
            },
            select(data) {
                return {
                    nodes: data.pages.flatMap((page) => page.nodes),
                    total: data.pages[data.pages.length - 1]?.total,
                };
            },
            ...options,
            ...initial,
        });
    };
    return hook;
}
const fetchMutation = async (name, params, opts) => {
    let url;
    let options = {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            ...opts?.headers,
            ...getApiKeyHeaders(),
        },
        body: JSON.stringify(params),
    };
    if (env.serverless && env.client) {
        // Relative request (in browser)
        url = `/_data/mutation/${name}`;
    }
    else if (env.serverless) {
        // Request to the origin server (server to server)
        url = joinURL(env.origin ?? "", `/wp-json/ed/v1/mutation/${name}`);
    }
    else {
        // Request straight to the wordpress route
        url = `/wp-json/ed/v1/mutation/${name}`;
    }
    // Apply API configuration
    if (apiConfig.customQueryFetchOptions) {
        options = await apiConfig.customQueryFetchOptions?.("mutation", name, params, url, options);
    }
    const response = await fetch(url, options);
    if (apiConfig.onResponse)
        apiConfig.onResponse(response, "mutation", name, params, url, options);
    const payload = await response.json();
    if (payload.errors?.length > 0) {
        throw new QueryError({
            messages: payload.errors.map((e) => e.message),
            statusCode: response.status,
            queryName: name,
            type: "mutation",
        });
    }
    return payload.data;
};
export function createUseMutation(init) {
    const hook = (options) => {
        return useMutation({
            mutationKey: ["mutation", init.name],
            mutationFn: (args) => fetchMutation(init.name, args, {
                headers: options?.headers,
            }),
            ...options,
        }, useFallbackClient());
    };
    hook.mutate = async (args, options) => {
        return fetchMutation(init.name, args, {
            headers: options?.headers,
        });
    };
    return hook;
}
// @ts-ignore
export function useRPCQuery(key, args, options) {
    return useQuery({
        queryKey: ["rpcQuery", key, args, options?.headers],
        queryFn: async (args) => {
            return getRPCClient().query(key, args.queryKey[2]);
        },
        ...options,
    }, useFallbackClient());
}
export function useRPCMutation(key, options) {
    return useMutation({
        mutationKey: ["rpcMutation", key],
        mutationFn: async (args) => {
            return getRPCClient().mutation(key, args);
        },
        ...options,
    }, useFallbackClient());
}
export const rpcClient = {
    query(name, input, opts) {
        return getRPCClient().query(name, input, opts);
    },
    mutation(name, input, opts) {
        return getRPCClient().mutation(name, input, opts);
    },
};
